import { takeEvery, select, getContext } from 'redux-saga/effects';
import { SET_DEAL_AFFILIATION_POPUP } from 'store/state/app/types';
import { RootAction } from 'store/state';
import { setDealAffiliationPopup } from 'store/state/app/actions';
import { routeSelector } from 'store/state/selectors/router';
import { ROUTE_TO_PAGE_TYPE } from 'analytics/constants';
import { DealAffiliationStatus } from 'utils/entities';
import { MUTATE, SET_MUTATION_RESPONSE } from 'store/state/mutationsResponse/types';
import { MutationType } from 'store/sagas/apiService/types';
import { SetMutationResponseAction } from 'store/state/mutationsResponse/actions';
import { get } from 'lodash';

const NEW_STATUS = 'new';
type AffiliationRequestStatusType = 'new' | DealAffiliationStatus;

const affiliationPopupPattern = (action: RootAction) => (
  action.type === SET_DEAL_AFFILIATION_POPUP
);

interface IPayload {
  source: string;
  request_status: AffiliationRequestStatusType;
}

let latestEventPayload: IPayload = null;

function* affiliationPopupWorker(action: ReturnType<typeof setDealAffiliationPopup>) {
  const { sendEvent } = yield getContext('analytics');

  if (!action.payload && !latestEventPayload) {
    return;
  }

  const route = yield select(routeSelector);

  const source = ROUTE_TO_PAGE_TYPE[route.name] || 'unknown_page';

  const status = action.payload ? (
    action.payload.affiliationRequest && action.payload.affiliationRequest.affiliationStatus
      ? action.payload.affiliationRequest.affiliationStatus
      : NEW_STATUS
  ) : latestEventPayload.request_status;

  const event = action.payload ? {
    source,
    request_status: status,
  } : latestEventPayload;

  latestEventPayload = event;

  if (action.payload) {
    sendEvent('deal_affiliation_form_open', 'deal_affiliation', { event });
  }
  else if (latestEventPayload) {
    sendEvent('deal_affiliation_form_close', 'deal_affiliation', { event });
    latestEventPayload = null;
  }
}


const affiliationSubmitPattern = (action: RootAction) =>
  action.type === MUTATE && (
    action.mutationType === MutationType.RequestDealAffiliation ||
    action.mutationType === MutationType.EditDealAffiliationRequest
  );

function* affiliationSubmitWorker(action: RootAction) {
  const { sendEvent } = yield getContext('analytics');

  sendEvent('deal_affiliation_form_submit', 'deal_affiliation', { event: latestEventPayload });
}

const affiliationSetResponsePattern = (action: RootAction) =>
  action.type === SET_MUTATION_RESPONSE && (
    action.mutationType === MutationType.RequestDealAffiliation ||
    action.mutationType === MutationType.EditDealAffiliationRequest
  );

function* affiliationSetResponseWorker(action: SetMutationResponseAction<MutationType.RequestDealAffiliation> | SetMutationResponseAction<MutationType.EditDealAffiliationRequest>) {
  const { sendEvent } = yield getContext('analytics');

  const response = action.meta.response;
  let hasError = false;

  if (action.mutationType === MutationType.EditDealAffiliationRequest) {
    const data = get(response, 'data.publicEditDealAffiliationRequest');
    hasError = !data || !data.soldId || Boolean(data.errorMessage);
  }
  else if (action.mutationType === MutationType.RequestDealAffiliation) {
    const data = get(response, 'data.requestDealAffiliation');
    hasError = !data || !data.soldId || Boolean(data.errorMessage);
  }

  if (hasError) {
    sendEvent('deal_affiliation_form_submit_failed', 'deal_affiliation', { event: latestEventPayload });
  }
  else {
    sendEvent('deal_affiliation_form_submit_success', 'deal_affiliation', { event: latestEventPayload });
    latestEventPayload = null;
  }
}

export function* dealAffiliationWorker() {
  yield takeEvery(affiliationPopupPattern, affiliationPopupWorker);
  yield takeEvery(affiliationSubmitPattern, affiliationSubmitWorker);
  yield takeEvery(affiliationSetResponsePattern, affiliationSetResponseWorker);
}
